import { useState, type FormEvent } from 'react';

import { joinRoom, type RoomPreview } from '../api.js';
import { readPreferredName, writePreferredName, writeSeat, type StoredSeat } from '../session.js';
import styles from './JoinForm.module.css';

export interface JoinFormProps {
  roomId: string;
  preview: RoomPreview | null;
  onJoined: (seat: StoredSeat) => void;
}

export function JoinForm({ roomId, preview, onJoined }: JoinFormProps) {
  const [name, setName] = useState(readPreferredName());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Enter a name to take a seat.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const seat = await joinRoom(roomId, trimmed);
      const stored: StoredSeat = { ...seat, name: trimmed };
      writePreferredName(trimmed);
      writeSeat(roomId, stored);
      onJoined(stored);
    } catch {
      setError('This lobby is closed, full, or already playing.');
      setBusy(false);
    }
  }

  const closed = preview !== null && !preview.joinable;

  return (
    <form className={styles.panel} onSubmit={onSubmit}>
      <h1 className={styles.title}>Take a seat</h1>
      <p className={styles.blurb}>
        {preview
          ? `${preview.seatCount} of ${preview.maxPlayers} seats taken.`
          : 'Checking the lobby…'}
      </p>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="join-name">
          Your name
        </label>
        <input
          id="join-name"
          className="lf-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={24}
          autoComplete="nickname"
        />
      </div>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      {closed && !error ? (
        <p className={styles.error} role="alert">
          This lobby is not accepting new players.
        </p>
      ) : null}

      <button className="lf-button lf-button--primary" type="submit" disabled={busy || closed}>
        {busy ? 'Joining…' : 'Join the game'}
      </button>
    </form>
  );
}
